import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import Magnetic from "@/components/Magnetic";

const LINKS = [
  { label: "Services", target: "services", type: "hash" },
  { label: "Studio", target: "studio", type: "hash" },
  { label: "Journal", to: "/blog", type: "route" },
  { label: "Careers", to: "/careers", type: "route" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goHash = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 350);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[100] transition-colors duration-300 border-b ${
        scrolled ? "bg-black/90 backdrop-blur-2xl border-white/10" : "bg-transparent border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-24 md:h-28 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-1 group" data-testid="nav-logo" onClick={() => setOpen(false)}>
          <span className="font-display text-2xl md:text-3xl uppercase tracking-tight text-white">Liinex</span>
          <span className="w-2 h-2 bg-[color:var(--accent)] group-hover:rotate-45 transition-transform" />
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {LINKS.map((l) =>
            l.type === "hash" ? (
              <button
                key={l.label}
                onClick={() => goHash(l.target)}
                className="font-mono text-xs tracking-[0.2em] uppercase text-white/60 hover:text-white transition-colors"
                data-testid={`nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </button>
            ) : (
              <Link
                key={l.label}
                to={l.to}
                className="font-mono text-xs tracking-[0.2em] uppercase text-white/60 hover:text-white transition-colors"
                data-testid={`nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </Link>
            )
          )}
        </nav>

        <div className="hidden md:block">
          <Magnetic>
            <Link
              to="/contact"
              data-testid="nav-contact-cta"
              className="inline-flex items-center gap-2 bg-white text-black px-6 py-3 font-mono text-xs tracking-[0.2em] uppercase hover:bg-[color:var(--accent)] hover:text-white transition-colors"
            >
              Start a project
              <span className="w-1.5 h-1.5 bg-[color:var(--accent)] group-hover:bg-white" />
            </Link>
          </Magnetic>
        </div>

        <button
          className="md:hidden text-white"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden bg-black border-t border-white/10 overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {LINKS.map((l) =>
                l.type === "hash" ? (
                  <button
                    key={l.label}
                    onClick={() => goHash(l.target)}
                    className="text-left font-display text-3xl uppercase text-white"
                  >
                    {l.label}
                  </button>
                ) : (
                  <Link
                    key={l.label}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className="font-display text-3xl uppercase text-white"
                  >
                    {l.label}
                  </Link>
                )
              )}
              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="mt-2 bg-white text-black px-6 py-4 text-center font-mono text-xs tracking-[0.2em] uppercase"
                data-testid="mobile-nav-contact-cta"
              >
                Start a project
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
